import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AcknowledgeModal, ActionModal, Modal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AgentApproveBatchModalProps } from './AgentApproveBatchModal.types'

export function AgentApproveBatchModal({
  isOpen,
  onClose,
  disbursementBatchId,
  refreshData
}: Readonly<AgentApproveBatchModalProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()

  const [status, setStatus] = useState<'error' | 'success' | null>(null)

  const [{ isPending }, { mutateAsync: agentApproveBatch }] = useMutation(disbursementsService.agentApproveBatch)

  const handleClose = () => {
    if (isPending) {
      return
    }

    setStatus(null)
    refreshData()
    onClose()
  }

  const handleSubmit = async () => {
    try {
      await agentApproveBatch(disbursementBatchId)
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  const successModal = status === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.agentApproveBatch.success.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="agent-approve-batch-success-modal"
      buttonTestId="agent-approve-batch-success-modal-close"
    />
  )

  const errorModal = status === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.agentApproveBatch.error.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="agent-approve-batch-error-modal"
      buttonTestId="agent-approve-batch-error-modal-close"
    />
  )

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.agentApproveBatch.title')}
          primaryButtonText={isPending ? t('button.approving') : t('button.approveAll')}
          primaryBtnTestId="agent-approve-batch-modal-submit"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="agent-approve-batch-modal-cancel"
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          loading={isPending}
        >
          <Text as="p" size="small" color="gray600">
            {t('dialog.agentApproveBatch.description')}
          </Text>
        </ActionModal>
      )}
    </Modal>
  )
}
